import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { createApiClient } from "@/config/createApiClient";
import { selectedClientServiceIdAtom } from "@/store";

import { RetroplanningApi } from "../../config/apiClient";

import { useWithTrackingMutation } from "./useWithTrackingMutation";

const apiClient = createApiClient(RetroplanningApi);

export const useRetroplanning = () => {
  const selectedClientServiceIdAtomValue = useAtomValue(
    selectedClientServiceIdAtom
  );

  const queryClient = useQueryClient();

  const useAddRetroplanning = (params: {
    orderId: string;
    orderItemId: string;
  }) =>
    useMutation({
      mutationKey: ["retroplanning", params],
      mutationFn: (
        body: Parameters<
          typeof apiClient.retroplanningsControllerCreate
        >["0"]["retroplanningItemCreateOrUpdateCommand"]
      ) =>
        apiClient.retroplanningsControllerCreate({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderItemId: params.orderItemId,
          retroplanningItemCreateOrUpdateCommand: body,
        }),
      onSuccess: () =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
        ]),
    });

  const useUpdateRetroplanning = (params: {
    orderId: string;
    orderItemId: string;
  }) => {
    return useMutation({
      mutationKey: ["retroplanning"],
      mutationFn: (
        body: Parameters<
          typeof apiClient.retroplanningsControllerUpdate
        >["0"]["retroplanningItemCreateOrUpdateCommand"]
      ) =>
        apiClient.retroplanningsControllerUpdate({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderItemId: params.orderItemId,
          retroplanningItemCreateOrUpdateCommand: body,
        }),
      onSuccess: () =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({ queryKey: ["retroplanning", params] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
        ]),
    });
  };

  const useShareRetroplanning = () => {
    return useWithTrackingMutation(
      (params: { orderId: string; orderItemId: string }) =>
        apiClient.retroplanningsControllerUpdateIsSharedAt({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderItemId: params.orderItemId,
        }),
      [
        {
          name: "validate_retroplanning_date",
          getMetaData: (_, data) => ({
            item_id: data.orderItemId,
          }),
        },
      ],
      {
        mutationKey: ["retroplanning"],
        onSuccess: (_, params) =>
          Promise.all([
            queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
            queryClient.refetchQueries({ queryKey: ["retroplanning", params] }),
            queryClient.invalidateQueries({
              queryKey: ["orderItem", params.orderId, params.orderItemId],
            }),
            queryClient.refetchQueries({
              queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
            }),
            queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          ]),
      }
    );
  };

  return {
    useAddRetroplanning,
    useUpdateRetroplanning,
    useShareRetroplanning,
  };
};
